import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, ClipboardList, Clock, CheckCheck, Search, ArrowUpRight } from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { procurementService } from '@/services/procurement';
import type { PurchaseRequest, PurchaseRequestStatus } from '@/types/procurement';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { PurchaseRequestDetailSheet } from './PurchaseRequestDetailSheet';
import { PurchaseRequestFormSheet } from './PurchaseRequestFormSheet';

type StatusFilter = 'all' | PurchaseRequestStatus;

export const PR_STATUS_LABEL: Record<PurchaseRequestStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  SOURCED: 'Sourced',
  RECEIVED: 'Received',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export const PR_STATUS_TONE: Record<
  PurchaseRequestStatus,
  'muted' | 'success' | 'warning' | 'destructive'
> = {
  DRAFT: 'muted',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'success',
  REJECTED: 'destructive',
  SOURCED: 'warning',
  RECEIVED: 'success',
  CLOSED: 'success',
  CANCELLED: 'destructive',
};

const SOURCING_LABEL: Record<PurchaseRequest['sourcingType'], string> = {
  VENDOR: 'Vendor',
  OPEN_MARKET: 'Open market',
};

export function PurchaseRequestsPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<PurchaseRequest | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [density, setDensity] = useState<TableDensity>('compact');
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['purchase-requests', { q: debounced }],
    queryFn: () =>
      procurementService.listRequests({ search: debounced || undefined, pageSize: 200 }),
  });

  const allRows = useMemo(() => data?.rows ?? [], [data]);

  const counts = useMemo(() => {
    const c = { all: allRows.length } as Record<StatusFilter, number>;
    (Object.keys(PR_STATUS_LABEL) as PurchaseRequestStatus[]).forEach((s) => (c[s] = 0));
    for (const r of allRows) c[r.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(
    () => (filter === 'all' ? allRows : allRows.filter((r) => r.status === filter)),
    [allRows, filter],
  );

  const summary = useMemo(() => {
    const pending = counts.PENDING_APPROVAL ?? 0;
    const open = allRows.filter(
      (r) => !['RECEIVED', 'CLOSED', 'CANCELLED', 'REJECTED'].includes(r.status),
    );
    return {
      pending,
      openCount: open.length,
      received: counts.RECEIVED ?? 0,
    };
  }, [allRows, counts]);

  const tabs: StatusTabOption[] = [
    { value: 'all', label: 'All', count: counts.all },
    { value: 'DRAFT', label: 'Draft', count: counts.DRAFT },
    { value: 'PENDING_APPROVAL', label: 'Pending', count: counts.PENDING_APPROVAL },
    { value: 'APPROVED', label: 'Approved', count: counts.APPROVED },
    { value: 'SOURCED', label: 'Sourced', count: counts.SOURCED },
    { value: 'RECEIVED', label: 'Received', count: counts.RECEIVED },
    { value: 'REJECTED', label: 'Rejected', count: counts.REJECTED },
  ];

  const columns: Column<PurchaseRequest>[] = [
    {
      key: 'pr',
      header: 'PR #',
      width: '180px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] font-medium text-foreground">{r.prNumber}</div>
          <div className="text-[10.5px] text-muted-foreground">{fmt.relative(r.createdAt)}</div>
        </div>
      ),
    },
    {
      key: 'sourcing',
      header: 'Sourcing',
      width: '130px',
      cell: (r) => (
        <div className="space-y-0.5">
          <Badge variant="muted" className="text-[10px]">
            {SOURCING_LABEL[r.sourcingType]}
          </Badge>
          {r.proposedVendor && (
            <div className="text-[11px] text-muted-foreground">{r.proposedVendor.name}</div>
          )}
        </div>
      ),
    },
    {
      key: 'warehouse',
      header: 'Warehouse',
      width: '150px',
      cell: (r) => (
        <div className="text-[12.5px] text-foreground">{r.warehouse.name ?? r.warehouse.code}</div>
      ),
    },
    {
      key: 'lines',
      header: 'Items',
      align: 'right',
      width: '70px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums text-muted-foreground">
          {r.items.length || '—'}
        </span>
      ),
    },
    {
      key: 'est',
      header: 'Est. total',
      align: 'right',
      width: '120px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums">
          {r.estimatedTotal != null ? fmt.inr(r.estimatedTotal) : '—'}
        </span>
      ),
    },
    {
      key: 'needed',
      header: 'Needed by',
      width: '140px',
      cell: (r) =>
        r.neededBy ? (
          <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
            {fmt.date(r.neededBy)}
          </span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '140px',
      cell: (r) => (
        <Badge variant={PR_STATUS_TONE[r.status]} className="text-[10px]">
          {PR_STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '60px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => setSelected(r)}
              >
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open</TooltipContent>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader
          title="Purchase Requests"
          count={counts.all}
          actions={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              New request
            </Button>
          }
        />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard
            label="Awaiting approval"
            value={fmt.number(summary.pending)}
            tone={summary.pending ? 'warning' : 'default'}
            icon={Clock}
          />
          <KpiCard label="Open requests" value={fmt.number(summary.openCount)} tone="primary" icon={ClipboardList} />
          <KpiCard label="Received" value={fmt.number(summary.received)} tone="default" icon={CheckCheck} />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PR # …"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={setSelected}
            density={density}
            emptyTitle={filter === 'all' ? 'No purchase requests yet' : 'Nothing in this status'}
            emptyDescription={
              filter === 'all'
                ? 'Raise a request for raw materials — it routes to a manager for approval.'
                : 'Try a different status filter.'
            }
            emptyAction={
              filter === 'all' ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  New request
                </Button>
              ) : null
            }
          />
        )}

        <PurchaseRequestDetailSheet
          prId={selected?.id ?? null}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
        />

        <PurchaseRequestFormSheet open={createOpen} onOpenChange={setCreateOpen} />
      </div>
    </TooltipProvider>
  );
}
